import React, { useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Input,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../../icons";
import { Check as CheckIcon } from "../../icons";
import { Storefront as StorefrontIcon } from "../../icons";
import { db, uploadImage } from "../../firebase";
import { brandColors } from "../../theme";
import { T } from "../Translate";
import LocationPicker from "../../components/LocationPicker";
import { geocodeAddress, reverseGeocodeAddress } from "../../utils/geo";

// Paleta acotada de acento para que el dueño personalice su tienda sin
// arriesgar combinaciones ilegibles sobre el fondo oscuro de la app.
export const STORE_ACCENT_SWATCHES = [
  brandColors.accent,
  "#7ee787",
  "#ffb84d",
  "#ff8a8a",
  "#c792ea",
  "#66d9ef",
  "#f4a5c0",
  "#f5e08a",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Edición inline de datos + apariencia de la tienda (sin navegar a otra
// página): mismos campos y reglas que valida CreateStore.js para la
// creación, más logoUrl y accentColor para personalizar la vidriera.
const StoreAppearanceForm = ({ storeSlug, storeData, onSaved }) => {
  const [form, setForm] = useState({
    storeName: storeData.storeName || "",
    description: typeof storeData.description === "string" ? storeData.description : "",
    address: storeData.address || "",
    hours: storeData.hours || "",
    contactEmail: storeData.contactEmail || storeData.ownerEmail || "",
    whatsapp: storeData.whatsapp || "",
    paypalEmail: storeData.paypalEmail || "",
    logoUrl: storeData.logoUrl || "",
    bannerUrl: storeData.bannerUrl || "",
    accentColor: storeData.accentColor || brandColors.accent,
    mostrarEstadisticas: storeData.mostrarEstadisticas !== false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [coords, setCoords] = useState(
    typeof storeData.latitude === "number" && typeof storeData.longitude === "number"
      ? { latitude: storeData.latitude, longitude: storeData.longitude }
      : null
  );
  const [locating, setLocating] = useState(false);
  // Evita que una respuesta vieja de reverse geocoding pise la dirección
  // si el usuario movió el pin varias veces seguidas.
  const lastPickRef = useRef(0);

  // El usuario marcó un punto en el mapa: se guarda esa posición exacta y
  // se completa la dirección con la calle correspondiente.
  const handlePickLocation = async (latitude, longitude) => {
    setCoords({ latitude, longitude });
    const pickId = ++lastPickRef.current;
    setLocating(true);
    const found = await reverseGeocodeAddress(latitude, longitude);
    if (pickId !== lastPickRef.current) return;
    setLocating(false);
    if (found) setForm((prev) => ({ ...prev, address: found }));
  };

  // Ubica en el mapa la dirección escrita en el campo.
  const handleSearchAddress = async () => {
    const cleanAddress = form.address.trim();
    if (!cleanAddress) {
      setErrors((prev) => ({ ...prev, address: <T>Ingresa la dirección de tu tienda.</T> }));
      return;
    }
    lastPickRef.current++;
    setLocating(true);
    const found = await geocodeAddress(cleanAddress);
    setLocating(false);
    if (found) {
      setCoords(found);
      setErrors((prev) => ({ ...prev, address: undefined }));
    } else {
      setErrors((prev) => ({ ...prev, address: <T>No encontramos esa dirección. Marcá la ubicación en el mapa.</T> }));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      setForm((prev) => ({ ...prev, logoUrl: URL.createObjectURL(file) }));
    }
  };

  const handleBannerChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBannerFile(file);
    if (file) {
      setForm((prev) => ({ ...prev, bannerUrl: URL.createObjectURL(file) }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cleanName = form.storeName.trim();
    const cleanDescription = form.description.trim();
    const cleanAddress = form.address.trim();
    const cleanContactEmail = form.contactEmail.trim();
    const cleanWhatsapp = form.whatsapp.trim();
    const cleanPaypalEmail = form.paypalEmail.trim();

    const validationErrors = {};
    if (!cleanName) validationErrors.storeName = <T>Ingresa un nombre para tu tienda.</T>;
    if (!cleanDescription) validationErrors.description = <T>Describe brevemente tu tienda.</T>;
    if (!cleanAddress) validationErrors.address = <T>Ingresa la dirección de tu tienda.</T>;
    if (cleanContactEmail && !EMAIL_RE.test(cleanContactEmail)) {
      validationErrors.contactEmail = <T>Ingresa un correo válido de contacto.</T>;
    }
    if (cleanWhatsapp && !/^\+?\d{8,15}$/.test(cleanWhatsapp.replace(/\s|-/g, ""))) {
      validationErrors.whatsapp = <T>Ingresa un número de WhatsApp válido (ej: +5491122334455).</T>;
    }
    if (cleanPaypalEmail && !EMAIL_RE.test(cleanPaypalEmail)) {
      validationErrors.paypalEmail = <T>Ingresa un correo válido de PayPal.</T>;
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    setFeedback(null);
    try {
      let logoUrl = form.logoUrl;
      if (logoFile) {
        logoUrl = await uploadImage(logoFile);
      }
      let bannerUrl = form.bannerUrl;
      if (bannerFile) {
        bannerUrl = await uploadImage(bannerFile);
      }

      // Si nunca se marcó en el mapa, se ubica a partir de la dirección.
      const location = coords || (await geocodeAddress(cleanAddress));

      const updates = {
        storeName: cleanName,
        description: cleanDescription,
        address: cleanAddress,
        hours: form.hours.trim(),
        contactEmail: cleanContactEmail,
        whatsapp: cleanWhatsapp,
        paypalEmail: cleanPaypalEmail,
        logoUrl,
        bannerUrl,
        accentColor: form.accentColor,
        mostrarEstadisticas: form.mostrarEstadisticas,
      };
      if (location) {
        updates.latitude = location.latitude;
        updates.longitude = location.longitude;
      }

      await db.collection("stores").doc(storeSlug).update(updates);
      setForm((prev) => ({ ...prev, logoUrl, bannerUrl }));
      setLogoFile(null);
      setBannerFile(null);
      setFeedback({ type: "success", message: <T>Cambios guardados.</T> });
      onSaved?.(updates);
    } catch (error) {
      console.error("Error al guardar la tienda:", error);
      setFeedback({ type: "error", message: <T>No pudimos guardar los cambios.</T> });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
    >
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            <T>Logo de la tienda</T>
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={form.logoUrl} sx={{ width: 64, height: 64, bgcolor: brandColors.primary }}>
              <StorefrontIcon />
            </Avatar>
            <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
              <T>Subir logo</T>
              <Input type="file" accept="image/*" onChange={handleLogoChange} sx={{ display: "none" }} />
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            <T>Foto de portada</T>
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>Se muestra como imagen ancha arriba de tu vidriera pública.</T>
          </Typography>
          {form.bannerUrl && (
            <Box
              component="img"
              src={form.bannerUrl}
              alt=""
              sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 2 }}
            />
          )}
          <Box>
            <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
              {form.bannerUrl ? <T>Cambiar portada</T> : <T>Subir portada</T>}
              <Input type="file" accept="image/*" onChange={handleBannerChange} sx={{ display: "none" }} />
            </Button>
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            <T>Color de acento</T>
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>Se usa en los botones y precios de tu vidriera pública.</T>
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
            {STORE_ACCENT_SWATCHES.map((color) => {
              const selected = form.accentColor === color;
              return (
                <Box
                  key={color}
                  role="button"
                  aria-label={color}
                  onClick={() => setForm((prev) => ({ ...prev, accentColor: color }))}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    backgroundColor: color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: selected ? "2px solid #fff" : "2px solid transparent",
                    boxShadow: selected ? "0 0 0 2px rgba(255,255,255,0.3)" : "none",
                  }}
                >
                  {selected && <CheckIcon sx={{ fontSize: 18, color: "#111" }} />}
                </Box>
              );
            })}
          </Stack>
        </Stack>

        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Switch
                checked={form.mostrarEstadisticas}
                onChange={(e) => setForm((prev) => ({ ...prev, mostrarEstadisticas: e.target.checked }))}
              />
            }
            label={<T>Mostrar calificación y reseñas públicamente</T>}
          />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>Si lo desactivás, los visitantes no ven el promedio ni las reseñas de tu tienda (las ventas y comisiones nunca se muestran públicamente, se ven solo en tu panel).</T>
          </Typography>
        </Stack>

        <TextField
          label={<T>Nombre de la tienda</T>}
          name="storeName"
          value={form.storeName}
          onChange={handleChange}
          error={Boolean(errors.storeName)}
          helperText={errors.storeName}
          required
        />
        <TextField
          label={<T>Descripción</T>}
          name="description"
          value={form.description}
          onChange={handleChange}
          error={Boolean(errors.description)}
          helperText={errors.description}
          multiline
          minRows={2}
          required
        />
        <TextField
          label={<T>Dirección</T>}
          name="address"
          value={form.address}
          onChange={handleChange}
          error={Boolean(errors.address)}
          helperText={errors.address}
          required
        />
        <LocationPicker
          latitude={coords?.latitude}
          longitude={coords?.longitude}
          onPick={handlePickLocation}
          onSearchAddress={handleSearchAddress}
          busy={locating}
          disabled={saving}
        />
        <TextField
          label={<T>Horarios de atención</T>}
          name="hours"
          value={form.hours}
          onChange={handleChange}
        />
        <TextField
          label={<T>Email de contacto</T>}
          name="contactEmail"
          value={form.contactEmail}
          onChange={handleChange}
          error={Boolean(errors.contactEmail)}
          helperText={errors.contactEmail}
        />
        <TextField
          label={<T>WhatsApp</T>}
          name="whatsapp"
          value={form.whatsapp}
          onChange={handleChange}
          error={Boolean(errors.whatsapp)}
          helperText={errors.whatsapp}
          placeholder="+5491122334455"
        />

        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            <T>Pagos con PayPal</T>
          </Typography>
          <Alert severity={form.paypalEmail ? "success" : "warning"}>
            {form.paypalEmail
              ? <T>Los cobros se acreditan a este correo.</T>
              : <T>Agrega un correo de PayPal para poder recibir pagos en tu tienda.</T>}
          </Alert>
          <TextField
            label={<T>Correo de PayPal</T>}
            name="paypalEmail"
            value={form.paypalEmail}
            onChange={handleChange}
            error={Boolean(errors.paypalEmail)}
            helperText={errors.paypalEmail || <T>Acá recibís el 100% de cada venta.</T>}
          />
        </Stack>

        <Box>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <T>Guardando...</T> : <T>Guardar cambios</T>}
          </Button>
        </Box>

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default StoreAppearanceForm;
